/* =========================================================
   AV Fullscreen Video Hero
   - supports multiple instances
   - image-first LCP + deferred video play
   - NO parallax
========================================================= */

(() => {
  const toBool = (v) => String(v) === "true";
  const toInt = (v, fallback) => {
    const n = parseInt(v, 10);
    return Number.isFinite(n) ? n : fallback;
  };

  const initOne = (root) => {
    if (!root || root.dataset.avInit === "true") return;
    root.dataset.avInit = "true";

    // ============================
    // VIDEO DEFER (image-first LCP)
    // ============================
    const shouldDefer = toBool(root.dataset.avDeferVideoLoad);
    if (!shouldDefer) return;

    // With a phone video set, each <video> carries data-av-media; only the one for
    // this screen is loaded and played (CSS hides the other), so phones never
    // download the desktop file and vice versa.
    const isPhone = window.matchMedia && window.matchMedia("(max-width: 749px)").matches;
    const v =
      root.querySelector('video.av-hero-video__video[data-av-media="' + (isPhone ? "mobile" : "desktop") + '"]') ||
      root.querySelector('video.av-hero-video__video:not([data-av-media="mobile"]):not([data-av-media="desktop"])');
    if (!v) return;

    const startWhenVisible = toBool(root.dataset.avVideoStartWhenVisible);
    let startDelay = toInt(root.dataset.avVideoStartDelayMs, 600);
    if (startDelay < 0) startDelay = 600;

    let armed = false;

    const armAndPlay = () => {
      if (armed) return;
      armed = true;

      // Respect reduced-motion: don't fetch or autoplay the background video; the
      // poster image (already shown) stays. Matches the prefers-reduced-motion
      // handling used elsewhere in the theme (marquees, review slider).
      if (window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

      // Data Saver or a slow connection: keep the poster and skip the multi-MB video.
      const conn = navigator.connection;
      if (conn && (conn.saveData || /(^|-)2g$|^3g$/.test(conn.effectiveType || ""))) return;

      // MP4 URL branch: only now set src from data-src to avoid early network
      const sources = v.querySelectorAll("source[data-src]");
      for (let i = 0; i < sources.length; i++) {
        const s = sources[i];
        if (!s.getAttribute("src")) s.setAttribute("src", s.getAttribute("data-src"));
      }

      try { v.load(); } catch (e) {}

      // Fade the video in only once a real frame is on screen. play() and "playing"
      // can fire before the first frame paints, which shows a blank flash over the
      // poster. The poster stays fully visible underneath the whole time.
      let shown = false;
      const show = () => {
        if (shown) return;
        shown = true;
        root.classList.add("is-video-playing");
      };
      const showOnFrame = () => {
        if (typeof v.requestVideoFrameCallback === "function") {
          v.requestVideoFrameCallback(() => requestAnimationFrame(show));
        } else {
          const onTime = () => {
            if (v.currentTime > 0) {
              v.removeEventListener("timeupdate", onTime);
              requestAnimationFrame(show);
            }
          };
          v.addEventListener("timeupdate", onTime);
        }
      };

      const play = () => {
        if (!v.play) return;
        v.play().then(showOnFrame).catch(() => {});
      };

      if (v.readyState >= 2) play();
      else v.addEventListener("loadeddata", play, { once: true });
    };

    // Don't start fetching the multi-MB video while the poster (the LCP image) is
    // still downloading: on a slow connection the two would share bandwidth and the
    // poster would arrive later. Wait for the poster to finish, capped at 2 s so a
    // stalled poster can't hold the video back, then apply the configured delay.
    // On a fast connection the poster is already complete here, so nothing changes.
    const whenPosterReady = (cb) => {
      const poster = root.querySelector("img.av-hero-video__poster");
      if (!poster || poster.complete) { cb(); return; }
      let done = false;
      const fire = () => { if (done) return; done = true; cb(); };
      poster.addEventListener("load", fire, { once: true });
      poster.addEventListener("error", fire, { once: true });
      setTimeout(fire, 2000);
    };

    const scheduleArm = () => {
      whenPosterReady(() => setTimeout(armAndPlay, startDelay));
    };

    if (startWhenVisible && "IntersectionObserver" in window) {
      const io = new IntersectionObserver((entries) => {
        for (let i = 0; i < entries.length; i++) {
          if (entries[i].isIntersecting) {
            io.disconnect();
            scheduleArm();
            break;
          }
        }
      }, { root: null, threshold: 0.2 });

      io.observe(root);
    } else {
      scheduleArm();
    }
  };

  const scan = () => {
    const nodes = document.querySelectorAll('.av-hero-video[data-av-hero="true"]:not([data-av-init="true"])');
    for (let i = 0; i < nodes.length; i++) initOne(nodes[i]);
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", scan, { once: true });
  } else {
    scan();
  }

  document.addEventListener("shopify:section:load", scan);
})();
